import React, { useMemo } from 'react'
import RoomBreakdownDetails from './RoomBreakdownDetails'
import useOfferMetaData from '../../../../hooks/Offers/useOfferMetaData'
import { isBedbankSearchRoom, isLEOffer, isReservationSearchRoom } from 'lib/offer/offerTypes'
import useBedbankRoomBreakdown from '../../../../hooks/useBedbankRoomBreakdown'
import { getPackageRoomBreakdown } from 'lib/search/searchUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { excludeNullOrUndefined } from 'checkout/utils'
import { useMaldivesOffersPricingContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function SearchTileRoomBreakdown({
  offer,
  filters,
}: Props) {
  const metaData = useOfferMetaData(offer.id, filters)

  // Try to get package IDs from Maldives pricing context (for multi-room allocation)
  const maldivesPricing = useMaldivesOffersPricingContext()
  const maldivesPackageIds = maldivesPricing.pricingByOfferId[offer.id]?.packageIds

  const recommendedForText = useMemo(() => {
    const numberOfAdults = filters?.rooms?.reduce((sum, room) => sum + room.adults, 0) ?? 0
    const numberOfChildren = filters?.rooms?.reduce((sum, room) => sum + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0), 0) ?? 0
    return `Recommended for ${numberOfAdults} adults${numberOfChildren > 0 ? `, ${pluralizeToString('child', numberOfChildren)}` : ''}`
  }, [filters?.rooms])

  const packageIds = useMemo(() => {
    // Prefer Maldives pricing context (has proper multi-room allocation)
    if (maldivesPackageIds && maldivesPackageIds.length > 0) {
      return maldivesPackageIds
    }

    // Fall back to metadata from search results
    const metaDataRooms = metaData?.rooms.filter(isReservationSearchRoom) ?? []
    const metadataPackageIds = metaDataRooms.map(r => r.packageId)
    const filtersRoomCount = filters?.rooms?.length ?? 0
    const metadataRoomCount = metaDataRooms.length

    // Scale up package IDs when metadata has fewer rooms than filters
    if (metadataRoomCount > 0 && metadataRoomCount < filtersRoomCount) {
      const scaleFactor = Math.ceil(filtersRoomCount / metadataRoomCount)
      return Array(scaleFactor).fill(metadataPackageIds).flat().slice(0, filtersRoomCount)
    }

    return metadataPackageIds
  }, [maldivesPackageIds, metaData?.rooms, filters?.rooms?.length])

  const leRoomsList = useMemo(() =>
    getPackageRoomBreakdown(packageIds, isLEOffer(offer) ? offer.packages : []),
  [packageIds, offer])

  const bedbankRooms = useMemo(() =>
    metaData?.rooms
      .filter(isBedbankSearchRoom)
      .map(r => (r.rateId ? { rateId: r.rateId, propertyId: r.propertyId } : undefined))
      .filter(excludeNullOrUndefined) ?? [],
  [metaData?.rooms])

  const bedbankRoomsList = useBedbankRoomBreakdown(bedbankRooms, filters)

  const allRooms = useMemo(() =>
    [...leRoomsList, ...bedbankRoomsList],
  [leRoomsList, bedbankRoomsList])

  if (allRooms.length === 0) return null

  return (
    <RoomBreakdownDetails
      recommendedForText={recommendedForText}
      rooms={allRooms}
    />
  )
}

export default SearchTileRoomBreakdown
